import type { ReactNode } from 'react';
import { ErrorPanel } from '@cwos/ui-admin';
import { type AuthorityGate, gateSentence } from './authorityGate.js';

/**
 * TWO WAYS TO SAY THE REFUSAL, AND EXACTLY ONE OF THEM PER CALLER.
 *
 * Most sites want the card — a heading and a sentence — so `heading`/`detail`
 * build it here rather than at eleven call sites. Some already render
 * `<Refused>` from `@cwos/ui-admin` (the lock panel a list refuses into), and
 * re-housing those in a card would change what those screens look like for no
 * reason connected to this defect; they pass `refusedNode` instead.
 *
 * The union is what stops a caller supplying both and then wondering which one
 * shows. Nothing here is optional in the sense of "may be omitted": every
 * caller says the refusal exactly once.
 */
type GateNoticeProps = {
  gate: AuthorityGate;
  /**
   * A verb phrase completing "…may you <what>": "read the schedule", "see this
   * worker's time records". It is what the pending and unreadable sentences are
   * built from, so it must read as something the READER would do, not as a
   * permission key.
   */
  what: string;
  /**
   * Ask for the permission set again. Offered on `unknown` only: a refusal is
   * an answer, and a retry button beside an answer promises that pressing it
   * might produce a different one.
   */
  onRetry: () => void;
} & (
  | {
      /** The refusal heading. Rendered for `refused` and for no other state. */
      heading: string;
      /** The refusal's second sentence — which permission, and what it opens. */
      detail: ReactNode;
      refusedNode?: never;
    }
  | { refusedNode: ReactNode; heading?: never; detail?: never }
);

/**
 * WHAT A SCREEN OR A PANEL SHOWS WHEN THE DOOR IS NOT OPEN — all three
 * not-open answers, so no caller has to remember that there are three.
 *
 * ---------------------------------------------------------------------------
 * WHY A COMPONENT AND NOT A `RequirePermission`
 * ---------------------------------------------------------------------------
 * `RequirePermission` guards a ROUTE: it owns the whole subtree and can refuse
 * to render it. The seven sites in
 * `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md` are not routes.
 * Three are screens the router already let through (the key they charge is not
 * the key the route was gated on), three are TAB PANELS inside a profile, and
 * one is a cell in a list. None of them can be expressed as a wrapper without
 * moving the screen, and all seven had written the refusal inline — which is
 * exactly why all seven forgot the other two answers.
 *
 * So the shape is `authorityGate` (the four-state answer, from UI-ORGCHART) plus
 * this (the three not-open renderings). `gateOpen(gate)` stays the caller's
 * question; everything else about not being open lives here, once.
 *
 * ---------------------------------------------------------------------------
 * THE REFUSAL COPY IS THE CALLER'S, THE OTHER TWO ARE NOT
 * ---------------------------------------------------------------------------
 * `heading`/`detail` are props because a refusal is the one answer a screen
 * knows something specific about: which permission, and what the reader would
 * be getting. The pending and unreadable sentences come from `gateSentence` and
 * cannot be overridden, because those two are the ones that were missing, and a
 * per-caller string is a per-caller opportunity to word them back into an
 * accusation.
 *
 * `data-gate` carries the gate's kind onto the DOM. It is how a test tells
 * "checking" from "couldn't check" from "no" without matching prose, and it is
 * the same device `Superusers.tsx` and `ChartEditing.tsx` already use
 * (`data-superuser-gate`, `data-manage-gate`).
 */
export function GateNotice({ gate, what, heading, detail, refusedNode, onRetry }: GateNoticeProps) {
  switch (gate.kind) {
    case 'allowed':
      return null;
    case 'unresolved':
      // NOT a skeleton. `RequirePermission` can use one because it is the whole
      // route and there is nothing else on the page to be confused with; these
      // sites sit beside real content, and a row of shimmer bars beside a
      // populated screen reads as "this part is empty", which is the claim being
      // avoided. A sentence says which part is still being worked out.
      return (
        <section className="rn-card" data-gate="unresolved">
          <p className="rn-muted" role="status">
            {gateSentence(gate, what)}
          </p>
        </section>
      );
    case 'unknown':
      return (
        <div data-gate="unknown">
          <ErrorPanel message={gateSentence(gate, what) ?? undefined} onRetry={onRetry} />
        </div>
      );
    case 'refused':
      return (
        <div data-gate="refused">
          {refusedNode ?? (
            <section className="rn-card" role="status">
              <h2 className="rn-card-title">{heading}</h2>
              <p className="rn-muted">{detail}</p>
            </section>
          )}
        </div>
      );
  }
}
